import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

export interface ConfirmOptions {
  title: string;
  /** 写清影响范围，例如"将取消 37 个未完成作业"（ARCHITECTURE §20.4）。 */
  description: ReactNode;
  confirmText?: string;
  danger?: boolean;
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  /**
   * 结掉悬着的那个 promise。**每条路都必须经过这里**：挂着不结的 promise 意味着
   * 调用方的 `await confirm()` 永远不往下走（它后面常跟着一次 mutate 与一次刷新）。
   */
  const settle = useCallback((value: boolean) => {
    const resolve = resolver.current;
    resolver.current = null;
    resolve?.(value);
  }, []);

  const confirm = useCallback<Confirm>(
    (next) =>
      new Promise<boolean>((resolve) => {
        // 上一个还没决就又来一个（抽屉里连点两处）：按"再想想"把旧的结掉，不留悬着的
        settle(false);
        resolver.current = resolve;
        setOptions(next);
      }),
    [settle],
  );

  // provider 卸载（退出登录、整页换掉）时同样不留悬着的 promise
  useEffect(() => () => settle(false), [settle]);

  const close = (value: boolean) => {
    settle(value);
    setOptions(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={options !== null} onClose={() => close(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{options?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">{options?.description}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => close(false)}>再想想</Button>
          <Button
            variant="contained"
            color={options?.danger ? "error" : "primary"}
            onClick={() => close(true)}
            autoFocus
          >
            {options?.confirmText ?? "确认"}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Confirm {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm 必须在 ConfirmProvider 内使用");
  return ctx;
}
